if (!customElements.get("fourpaw-package-selector")) {
  customElements.define(
    "fourpaw-package-selector",
    class FourPawPackageSelector extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        this.cards = Array.from(this.querySelectorAll("[data-package-card]"));
        this.mainVariants = this.readJson("[data-fourpaw-main-variants]");
        this.mainOptions = this.readJson("[data-fourpaw-main-options]");
        this.bungeeVariants = this.readJson("[data-fourpaw-bungee-variants]");
        this.bungeeOptions = this.readJson("[data-fourpaw-bungee-options]");
        this.collarVariants = this.readJson("[data-fourpaw-collar-variants]");
        this.collarOptions = this.readJson("[data-fourpaw-collar-options]");
        this.colorOptionName = this.normalize(this.dataset.colorOptionName || "Color");
        this.sizeOptionName = this.normalize(this.dataset.sizeOptionName || "Size");
        this.giftMode = this.dataset.giftMode || "three_products";
        this.giftVariantId = Number(this.dataset.giftVariantId || 0);
        this.giftTagsVariantId = Number(this.dataset.giftTagsVariantId || 0);
        this.giftDispenserVariantId = Number(this.dataset.giftDispenserVariantId || 0);
        this.giftRollVariantId = Number(this.dataset.giftRollVariantId || 0);
        this.giftAvailable = this.dataset.giftAvailable === "true";
        this.selectedPackage =
          this.querySelector("[data-package-input]:checked")?.value ||
          this.dataset.defaultPackage ||
          this.querySelector("[data-package-input]")?.value ||
          "harness_only";
        this.querySelectorAll("[data-package-product-image]").forEach((image) => {
          image.dataset.packageFallbackSrc = image.currentSrc || image.src || "";
        });
        this.sticky = this.querySelector("[data-package-sticky]");
        this.stickyButton = this.querySelector("[data-package-sticky-button]");
        if (this.sticky) {
          const componentStyles = window.getComputedStyle(this);
          [
            "--fourpaw-package-navy",
            "--fourpaw-package-gold",
            "--fourpaw-package-ink",
            "--fourpaw-package-muted",
            "--fourpaw-package-border",
            "--fourpaw-package-surface",
          ].forEach((property) => {
            this.sticky.style.setProperty(property, componentStyles.getPropertyValue(property));
          });
          document.body.append(this.sticky);
        }
        this.packageGroup = this.newGroup();
        this.prepareSubmit = () => {
          this.releaseRootFields();
          this.packageGroup = this.newGroup();
          const main = this.currentMainVariant();
          if (!main) return;
          const colorIndex = this.optionIndex(this.mainOptions, this.colorOptionName, 0);
          const sizeIndex = this.optionIndex(this.mainOptions, this.sizeOptionName, 1);
          const color = main.options?.[colorIndex];
          const size = main.options?.[sizeIndex];
          const bungee = this.matchingVariant(this.bungeeVariants, this.bungeeOptions, color, size, false);
          const collar = this.matchingVariant(this.collarVariants, this.collarOptions, color, size, true);
          this.syncFormLines(main, bungee, collar);
          this.suspendRootFields();
          window.setTimeout(() => this.releaseRootFields(), 0);
        };

        this.addEventListener("change", (event) => {
          if (!event.target.matches("[data-package-input]")) return;
          this.selectedPackage = event.target.value;
          this.update();
        });

        this.onVariantChange = () => window.setTimeout(() => this.update(), 0);
        document.addEventListener("simple-variant-picker:variantChange", this.onVariantChange);
        document.addEventListener("variant:change", this.onVariantChange);
        this.bindForm();

        this.onStickyClick = () => {
          const button = this.form?.querySelector('[type="submit"]');
          if (!button || button.disabled) return;
          if (typeof this.form.requestSubmit === "function") this.form.requestSubmit(button);
          else button.click();
        };
        this.stickyButton?.addEventListener("click", this.onStickyClick);

        window.setTimeout(() => {
          this.update();
          this.observePurchaseButton();
        }, 0);
      }

      disconnectedCallback() {
        document.removeEventListener("simple-variant-picker:variantChange", this.onVariantChange);
        document.removeEventListener("variant:change", this.onVariantChange);
        this.unbindForm();
        this.stickyButton?.removeEventListener("click", this.onStickyClick);
        this.purchaseObserver?.disconnect();
        if (this.onStickyPositionChange) {
          window.removeEventListener("scroll", this.onStickyPositionChange);
          window.removeEventListener("resize", this.onStickyPositionChange);
        }
        if (this.stickyPositionFrame) window.cancelAnimationFrame(this.stickyPositionFrame);
        this.releaseRootFields();
        this.sticky?.remove();
      }

      bindForm() {
        const nextForm = document.getElementById(this.dataset.productFormId);
        if (nextForm === this.boundForm) {
          this.form = nextForm;
          return;
        }

        this.unbindForm();
        this.form = nextForm;
        this.boundForm = nextForm;
        if (!this.boundForm) return;

        this.boundForm.addEventListener("submit", this.prepareSubmit, true);
        this.boundVariantInput = this.boundForm.querySelector('[name="id"]');
        this.boundVariantInput?.addEventListener("change", this.onVariantChange);
      }

      unbindForm() {
        this.boundForm?.removeEventListener("submit", this.prepareSubmit, true);
        this.boundVariantInput?.removeEventListener("change", this.onVariantChange);
        this.boundForm = null;
        this.boundVariantInput = null;
      }

      newGroup() {
        if (window.crypto?.randomUUID) return `fourpaw-${window.crypto.randomUUID()}`;
        return `fourpaw-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      readJson(selector) {
        const node = this.querySelector(selector);
        if (!node) return [];
        try {
          const parsed = JSON.parse(node.textContent || "[]");
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.warn("FourPaw package data could not be read.", error);
          return [];
        }
      }

      normalize(value) {
        return String(value || "").trim().toLowerCase();
      }

      optionIndex(options, name, fallback) {
        const index = options.findIndex((option) => this.normalize(option) === name);
        return index >= 0 ? index : fallback;
      }

      currentMainVariant() {
        const id = this.form?.querySelector('[name="id"]')?.value;
        return this.mainVariants.find((variant) => String(variant.id) === String(id)) || this.mainVariants[0] || null;
      }

      matchingVariant(variants, options, color, size, requiresSize) {
        const colorIndex = this.optionIndex(options, this.colorOptionName, 0);
        const sizeIndex = this.optionIndex(options, this.sizeOptionName, requiresSize ? 1 : -1);

        return (
          variants.find((variant) => {
            const colorMatches = this.normalize(variant.options?.[colorIndex]) === this.normalize(color);
            const sizeMatches =
              !requiresSize || this.normalize(variant.options?.[sizeIndex]) === this.normalize(size);
            return colorMatches && sizeMatches && variant.available;
          }) || null
        );
      }

      money(cents) {
        if (typeof window.Elixir_FormatMoney === "function") return window.Elixir_FormatMoney(cents);
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, window.Shopify.money_format);
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency: window.Shopify?.currency?.active || "USD",
        }).format(Number(cents || 0) / 100);
      }

      comparePrice(variant) {
        if (!variant) return 0;
        const compare = Number(variant.compare_at_price ?? variant.compareAtPrice ?? 0);
        return compare > 0 ? compare : Number(variant.price || 0);
      }

      packageData(kind, main, bungee, collar) {
        const variants = [main];
        if (kind === "harness_bungee" || kind === "full_set") variants.push(bungee);
        if (kind === "full_set") variants.push(collar);
        const upgradePrice =
          kind === "harness_bungee"
            ? Number(bungee?.price || 0)
            : kind === "full_set"
              ? Number(collar?.price || 0)
              : 0;

        const available =
          this.giftAvailable &&
          variants.every((variant) => variant && variant.available !== false);
        const price = variants.reduce((total, variant) => total + Number(variant?.price || 0), 0);
        const compare = variants.reduce((total, variant) => total + this.comparePrice(variant), 0);

        return {
          available,
          price,
          compare,
          savings: Math.max(0, compare - price),
          upgradePrice,
        };
      }

      variantImage(variant) {
        return variant?.featured_image?.src || variant?.featured_image?.url || variant?.featured_media?.preview_image?.src || "";
      }

      resizedImage(source, width = 144) {
        if (!source) return "";
        try {
          const url = new URL(source, window.location.origin);
          if (url.hostname.endsWith("cdn.shopify.com")) url.searchParams.set("width", String(width));
          return url.href;
        } catch {
          return source;
        }
      }

      syncCardMedia(card, main, bungee, collar) {
        const variants = { main, bungee, collar };
        card.querySelectorAll("[data-package-product-image]").forEach((image) => {
          const variantSource = this.resizedImage(
            this.variantImage(variants[image.dataset.packageProductImage]),
          );
          const source = variantSource || image.dataset.packageFallbackSrc;
          if (source && image.src !== source) image.src = source;
        });
      }

      syncCardRowPricing(card, main, bungee, collar) {
        const variants = { main, bungee, collar };

        card.querySelectorAll("[data-package-row-price]").forEach((price) => {
          const variant = variants[price.dataset.packageRowPrice];
          price.textContent = this.money(Number(variant?.price || 0));
        });

        card.querySelectorAll("[data-package-row-compare]").forEach((compare) => {
          const variant = variants[compare.dataset.packageRowCompare];
          const price = Number(variant?.price || 0);
          const comparePrice = this.comparePrice(variant);
          compare.textContent = this.money(comparePrice);
          compare.hidden = comparePrice <= price;
        });
      }

      syncCardExpansion() {
        this.cards.forEach((card) => {
          const contents = card.querySelector("[data-package-contents]");
          if (!contents) return;

          const input = card.querySelector("[data-package-input]");
          const expanded =
            card.dataset.packageKind === this.selectedPackage &&
            input?.checked === true &&
            input.disabled === false;
          card.setAttribute("aria-expanded", expanded ? "true" : "false");
          contents.hidden = !expanded;
        });
      }

      updateCard(card, data) {
        const input = card.querySelector("[data-package-input]");
        const status = card.querySelector("[data-package-status]");
        const price = card.querySelector("[data-package-price]");
        const compare = card.querySelector("[data-package-compare]");
        const savings = card.querySelector("[data-package-savings]");
        const increment = card.querySelector("[data-package-increment]");
        const savingsPrefix = this.dataset.savingsPrefix || "Save";
        const savingsSuffix = this.dataset.savingsSuffix || "";

        if (input) input.disabled = !data.available;
        card.setAttribute("aria-disabled", data.available ? "false" : "true");
        card.classList.toggle("is-selected", input?.checked === true);
        if (increment) increment.textContent = this.money(data.upgradePrice);
        if (price) price.textContent = this.money(data.price);
        if (compare) {
          compare.textContent = this.money(data.compare);
          compare.hidden = data.compare <= data.price;
        }
        if (savings) {
          savings.textContent = [savingsPrefix, this.money(data.savings), savingsSuffix]
            .filter(Boolean)
            .join(" ");
          savings.hidden = data.savings <= 0;
        }
        if (status) status.hidden = data.available;
      }

      hiddenInput(name, value, role) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = String(value);
        input.dataset.fourpawPackageLine = role;
        return input;
      }

      nextItemIndex() {
        let max = -1;
        this.form?.querySelectorAll('input[name^="items["]').forEach((input) => {
          if (input.dataset.fourpawPackageLine) return;
          const match = input.name.match(/^items\[(\d+)\]/);
          if (match) max = Math.max(max, Number(match[1]));
        });
        return max + 1;
      }

      addLine(index, variantId, role) {
        if (!this.form || !variantId) return index;
        const packageName = this.dataset.packagePropertyName || "SafeWalk package";
        this.form.append(
          this.hiddenInput(`items[${index}][id]`, variantId, role),
          this.hiddenInput(`items[${index}][quantity]`, 1, role),
          this.hiddenInput(
            `items[${index}][properties][_FourPaw package]`,
            `${packageName}: ${this.selectedPackage}`,
            role,
          ),
          this.hiddenInput(`items[${index}][properties][_FourPaw group]`, this.packageGroup, role),
          this.hiddenInput(`items[${index}][properties][_FourPaw role]`, role, role),
        );
        if (role.startsWith("free SafeWalk gift")) {
          this.form.append(
            this.hiddenInput(`items[${index}][properties][_is_free_gift]`, "true", role),
            this.hiddenInput(
              `items[${index}][properties][_linked_to_product]`,
              this.dataset.mainProductId,
              role,
            ),
          );
        }
        return index + 1;
      }

      copyMainProperties(index) {
        if (!this.form) return;
        Array.from(this.form.elements).forEach((control) => {
          if (
            !control.name?.startsWith("properties[") ||
            control.dataset.fourpawPackageLine ||
            control.disabled ||
            ((control.type === "checkbox" || control.type === "radio") && !control.checked)
          ) {
            return;
          }

          const propertyName = control.name.match(/^properties\[(.+)\]$/)?.[1];
          if (!propertyName) return;
          this.form.append(
            this.hiddenInput(
              `items[${index}][properties][${propertyName}]`,
              control.value,
              "main-personalization",
            ),
          );
        });
      }

      suspendRootFields() {
        if (!this.form) return;
        this.releaseRootFields();
        this.suspendedRootFields = Array.from(this.form.elements).filter(
          (control) =>
            !control.disabled &&
            !control.dataset.fourpawPackageLine &&
            (control.name === "id" ||
              control.name === "quantity" ||
              control.name?.startsWith("properties[")),
        );
        this.suspendedRootFields.forEach((control) => {
          control.disabled = true;
        });
      }

      releaseRootFields() {
        this.suspendedRootFields?.forEach((control) => {
          control.disabled = false;
        });
        this.suspendedRootFields = [];
      }

      syncFormLines(main, bungee, collar) {
        if (!this.form) return;
        this.form.querySelectorAll("[data-fourpaw-package-line]").forEach((input) => input.remove());

        let index = this.nextItemIndex();
        const mainIndex = index;
        index = this.addLine(index, main?.id, "SafeWalk harness");
        if (this.giftMode !== "three_products") this.copyMainProperties(mainIndex);
        if (this.selectedPackage === "harness_bungee" || this.selectedPackage === "full_set") {
          index = this.addLine(index, bungee?.id, "matching bungee leash");
        }
        if (this.selectedPackage === "full_set") {
          index = this.addLine(index, collar?.id, "matching padded collar");
        }
        if (this.giftAvailable && this.giftMode === "three_products") {
          const tagsIndex = index;
          index = this.addLine(index, this.giftTagsVariantId, "free SafeWalk gift tags");
          this.copyMainProperties(tagsIndex);
          index = this.addLine(index, this.giftDispenserVariantId, "free SafeWalk gift dispenser");
          this.addLine(index, this.giftRollVariantId, "free SafeWalk gift roll");
        } else if (this.giftAvailable && this.giftVariantId) {
          this.addLine(index, this.giftVariantId, "free SafeWalk gift pack");
        }
      }

      syncButton(total, available) {
        const button = this.form?.querySelector('[type="submit"]');
        const text = button?.querySelector(".button-text");
        if (!button || !text) return;

        const label = this.dataset.buttonLabel || "Add selected package to cart";
        const unavailableLabel = this.dataset.buttonUnavailableLabel || "Selected package unavailable";
        button.dataset.persistentText = label;
        button.dataset.textOverride = "true";
        button.dataset.showPrice = "true";
        button.classList.toggle("fourpaw-package-unavailable", !available);
        button.disabled = !available;
        button.setAttribute("aria-disabled", available ? "false" : "true");
        text.textContent = available ? `${label} - ${this.money(total)}` : unavailableLabel;
      }

      syncSticky(data) {
        if (!this.sticky) return;
        const selectedCard = this.cards.find((card) => card.dataset.packageKind === this.selectedPackage);
        const title = selectedCard?.querySelector(".fourpaw-package__copy strong")?.textContent?.trim();
        const titleNode = this.sticky.querySelector("[data-package-sticky-title]");
        const priceNode = this.sticky.querySelector("[data-package-sticky-price]");
        if (titleNode && title) titleNode.textContent = title;
        if (priceNode) priceNode.textContent = this.money(data.price);
        if (this.stickyButton) this.stickyButton.disabled = !data.available;
      }

      observePurchaseButton() {
        const button = this.form?.querySelector('[type="submit"]');
        if (!button || !this.sticky) return;
        this.purchaseObserver?.disconnect();
        this.purchaseButton = button;
        if (!this.onStickyPositionChange) {
          this.onStickyPositionChange = () => {
            if (this.stickyPositionFrame) return;
            this.stickyPositionFrame = window.requestAnimationFrame(() => {
              this.stickyPositionFrame = null;
              this.syncStickyVisibility();
            });
          };
        }
        window.removeEventListener("scroll", this.onStickyPositionChange);
        window.removeEventListener("resize", this.onStickyPositionChange);
        window.addEventListener("scroll", this.onStickyPositionChange, { passive: true });
        window.addEventListener("resize", this.onStickyPositionChange);
        this.syncStickyVisibility();
      }

      syncStickyVisibility() {
        if (!this.purchaseButton || !this.sticky) return;
        const visible = this.purchaseButton.getBoundingClientRect().bottom <= 0;
        this.sticky.classList.toggle("is-visible", visible);
        this.sticky.setAttribute("aria-hidden", visible ? "false" : "true");
      }

      update() {
        this.bindForm();
        const main = this.currentMainVariant();
        if (!main) return;

        const mainColorIndex = this.optionIndex(this.mainOptions, this.colorOptionName, 0);
        const mainSizeIndex = this.optionIndex(this.mainOptions, this.sizeOptionName, 1);
        const color = main.options?.[mainColorIndex];
        const size = main.options?.[mainSizeIndex];
        const bungee = this.matchingVariant(this.bungeeVariants, this.bungeeOptions, color, size, false);
        const collar = this.matchingVariant(this.collarVariants, this.collarOptions, color, size, true);
        const packageMap = {
          harness_only: this.packageData("harness_only", main, bungee, collar),
          harness_bungee: this.packageData("harness_bungee", main, bungee, collar),
          full_set: this.packageData("full_set", main, bungee, collar),
        };

        this.cards.forEach((card) => {
          this.updateCard(card, packageMap[card.dataset.packageKind]);
          this.syncCardMedia(card, main, bungee, collar);
          this.syncCardRowPricing(card, main, bungee, collar);
        });

        if (!packageMap[this.selectedPackage]?.available) {
          const fallback = this.cards
            .map((card) => card.querySelector("[data-package-input]"))
            .find((input) => input && !input.disabled);
          if (fallback) {
            this.selectedPackage = fallback.value;
            fallback.checked = true;
            this.cards.forEach((card) =>
              card.classList.toggle("is-selected", card.dataset.packageKind === this.selectedPackage),
            );
          }
        }

        this.syncCardExpansion();
        this.syncFormLines(main, bungee, collar);
        const selected = packageMap[this.selectedPackage];
        if (!selected) return;
        this.syncSticky(selected);
        window.requestAnimationFrame(() => this.syncButton(selected.price, selected.available));
      }
    },
  );
}
